import { ManifestHandlerEvents } from '@/viewerExtensions/events';
import { ViewerEvents, getChildMeshList, unfreezeMeshListWorldMatrix, freezeMeshListWorldMatrix, BaseExtension } from '@immersive-tech/viewer-engine';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import type { Viewer, IViewerModelLoadedEvent, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest } from '@/types/IManifest';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Lights } from '@/viewerExtensions/lights';
import type { Perspectives } from '@/viewerExtensions/perspectives';

const environmentMaps = __ENVIRONMENT_MAPS__.map((envmap) => ({
  path: `/content/dam/LifeTech/virtual/common/envmap/${envmap}`, name: envmap,
}));

export class ManifestHandler extends BaseExtension {
  private envMapDownloaders: Set<Promise<string>>;
  public sceneJson: Nullable<IManifest>;
  public emptyManifest: IManifest;

  constructor(viewer: Viewer) {
    super(viewer);

    this.sceneJson = null;
    this.envMapDownloaders = new Set<Promise<string>>();
    this.emptyManifest = ManifestHandler.generateEmptyManifest();

    this.viewer.addEventListener(ManifestHandlerEvents.MANIFEST_LOADED, this.manifestLoadedHandler.bind(this));
    this.viewer.addEventListener(ViewerEvents.VIEWER_INITIALIZED, this.viewerInitializedHandler.bind(this));
    this.viewer.addEventListener(ViewerEvents.MODEL_LOADED, this.modelLoadedEventHandler.bind(this) as EventListener);
  }

  protected get LightsExtension() {
    return this.viewer.extensions.get('Lights') as Extension<Lights>;
  }

  protected get PerspectivesExtension() {
    return this.viewer.extensions.get('Perspectives') as Extension<Perspectives>;
  }

  public loadManifest(manifest: IManifest) {
    this.sceneJson = this.createValidManifest(manifest);

    this.viewer.dispatchViewerEvent(ManifestHandlerEvents.MANIFEST_LOADED, {
      data: this.sceneJson,
    });
  }

  public static generateEmptyManifest(): IManifest {
    return {
      name: '',
      envmap: environmentMaps[0].path,
      envMapIntensity: 1,
      envMapRotation: Math.PI / 2,
      basePath: '',
      xButtonURL: '',
      backgroundColor: '#ffffff',
      version: '2.0.2',
      fastRewind: false,
      hideXButton: false,
      hotspots: {},
      parts: [],
      views: {
        Main: {
          position: { x: 0, y: 0.6, z: 2 },
          target: { x: 0, y: 0, z: 0 },
          angle: null,
          zoomRestriction: [0, 10],
        },
      },
      lights: [
        {
          lightIntensity: 1,
          position: {
            x: 0,
            y: 1,
            z: 0,
          },
        },
      ],
      ctaButtons: [
        {
          text: '',
          path: '',
        },
        {
          text: '',
          path: '',
        },
      ],
      translations: {},
      meshFreezeOptimization: true,
      worldMatrixFreezeOptimization: true,
      materialFreezeOptimization: true,
      hideSmallMeshesOptimization: true,
      skybox: undefined,
      hideFloor: false,
      ar: {
        ios: undefined,
        android: undefined,
      },
    };
  }

  public createValidManifest(manifest: IManifest): IManifest {
    const validManifest = {
      ...this.emptyManifest,
      ...manifest,
    };

    if (Object.keys(validManifest.views).length === 0) {
      validManifest.views = {
        ...this.emptyManifest.views,
      };
    }

    const validManifestViewKeys = Object.keys(validManifest.views);

    for (const viewName of validManifestViewKeys) {
      validManifest.views[viewName] = {
        ...this.emptyManifest.views.Main,
        ...validManifest.views[viewName],
      };
    }

    const validManifestHotspotKeys = Object.keys(validManifest.hotspots);

    for (const hotspotName of validManifestHotspotKeys) {
      const hotspot = validManifest.hotspots[hotspotName];

      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < hotspot.position.length; ++i) {
        /* eslint-disable @typescript-eslint/no-unnecessary-condition */
        hotspot.position[i].x ??= 0;
        hotspot.position[i].y ??= 0;
        hotspot.position[i].z ??= 0;
        /* eslint-enable @typescript-eslint/no-unnecessary-condition */
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      hotspot.startVisible ??= true;

      for (let index = 0; index < hotspot.animations.length; ++index) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (hotspot.animations[index].length !== 2) {
          hotspot.animations.splice(index, 1);
        }
      }

      for (let index = 0; index < hotspot.hotspotUpdates.length; ++index) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (hotspot.hotspotUpdates[index].length !== 2) {
          hotspot.hotspotUpdates.splice(index, 1);
        }
      }

      hotspot.perspective = hotspot.perspective ?? null;
      hotspot.flatContentURL = hotspot.flatContentURL ?? null;
    }

    for (const light of validManifest.lights) {
      light.position = {
        ...this.emptyManifest.lights[0].position,
        ...light.position,
      };

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      light.lightIntensity ??= this.emptyManifest.lights[0].lightIntensity;
    }

    validManifest.ar = {
      ...this.emptyManifest.ar,
      ...validManifest.ar,
    }

    return validManifest;
  }

  private async manifestLoadedHandler() {
    if (!this.sceneJson) return;

    await Promise.all(this.envMapDownloaders);

    for (const [entityIdentifier] of this.viewer.modelContainers) {
      this.viewer.destroyModel(entityIdentifier);
    }

    const manifestEnvmap = this.sceneJson.envmap.split('/').pop() as string;

    if (environmentMaps.find((envmap) => envmap.name === manifestEnvmap)) {
      this.viewer.environmentMap = manifestEnvmap;
    } else {
      this.viewer.environmentMap = environmentMaps[0].name;
    }

    this.viewer.environmentMapRotation = this.sceneJson.envMapRotation;
    this.viewer.environmentMapIntensity = this.sceneJson.envMapIntensity;

    for (let i = 0; i < this.LightsExtension.lights.length; ++i) {
      this.LightsExtension.destroyLight(i);
    }

    for (let index = 0; index < this.sceneJson.lights.length; ++index) {
      const manifestLight = this.sceneJson.lights[index];
      const { lightIntensity } = manifestLight;
      const lightPosition = new Vector3(-manifestLight.position.x, -manifestLight.position.y, -manifestLight.position.z);
      const enableShadows = index === 0;

      this.LightsExtension.createLight(lightPosition, lightIntensity, enableShadows);
    }

    this.PerspectivesExtension.resetView();
  }

  private viewerInitializedHandler() {
    for (const environmentMap of environmentMaps) {
      this.envMapDownloaders.add(this.viewer.downloadEnvironmentMap(environmentMap.path, environmentMap.name, 512));
    }
  }

  private modelLoadedEventHandler(event: CustomEvent<IViewerModelLoadedEvent>) {
    if (!this.sceneJson) return;

    const entityIdentifier = event.detail.data;
    const entity = this.viewer.entities.get(entityIdentifier) as Nullable<Mesh>;
    const sceneJsonPart = this.sceneJson.parts.find((part) => part.name === entityIdentifier);

    if (entity && sceneJsonPart?.invert) {
      const meshes = [entity, ...getChildMeshList([entity])];

      freezeMeshListWorldMatrix(meshes);

      entity.scaling.x = -entity.scaling.x;
      entity.scaling.z = -entity.scaling.z;

      unfreezeMeshListWorldMatrix(meshes);
    }

    this.viewer.renderNextFrame += 1;
  }
}
